import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'react-intl';
import { template } from 'lodash-es';

const gatsbyConfigQuery = graphql`
  query SEO {
    site {
      pathPrefix
      siteMetadata {
        siteName
        canonicalUrl
        author
        titleSuffix
        imageHeight
        imageWidth
        imageUrlBase
        imageFileLookup {
          home
          plannerForms
          tripItinerary
          stopDetail
          vehicleTracking
          routeList
          routeDetail
          location
          alerts
          routeAlerts
          omnibox
          menu
        }
        twitterCard
        twitterSite
      }
    }
  }
`;

// Search Engine Optimization
function SEO({ metadataId, dynamicValues, useTitleSuffix, indexPage }) {
  const { site } = useStaticQuery(gatsbyConfigQuery);
  const {
    siteName,
    canonicalUrl,
    author,
    titleSuffix,
    imageHeight,
    imageWidth,
    imageUrlBase,
    imageFileLookup,
    twitterCard,
    twitterSite,
  } = site.siteMetadata;

  const intl = useIntl();
  const messageIdPrefix = `metadata.${metadataId}`;

  const title =
    intl.formatMessage({ id: `${messageIdPrefix}.title` }, dynamicValues) +
    (useTitleSuffix ? titleSuffix : '');

  const description = intl.formatMessage(
    { id: `${messageIdPrefix}.description` },
    dynamicValues
  );

  // use lodash's template method to inject dynamic values into
  // placeholders in the image url template
  const imageUrl = template(imageUrlBase + imageFileLookup[metadataId])(
    dynamicValues
  );

  const location = useLocation();

  return (
    <Helmet
      htmlAttributes={{ lang: intl.locale }}
      title={title}
      meta={[
        {
          name: 'robots',
          content: indexPage ? 'all' : 'none',
        },
        {
          name: 'description',
          content: description,
        },
        {
          name: 'keywords',
          content: intl.formatMessage({
            id: `${messageIdPrefix}.keywords`,
            // some pages don't have keywords and this default suppresses
            // react-intl errors for those cases
            defaultMessage: ' ',
          }),
        },
        {
          name: 'author',
          content: author,
        },
        {
          property: 'og:url',
          content:
            canonicalUrl +
            location.pathname.replace(
              // remove the path prefix that the app is running under from the
              // pathname as it should not be included in the canonical url
              new RegExp(`^${site.pathPrefix}`),
              ''
            ),
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          property: 'og:site_name',
          content: siteName,
        },
        {
          property: 'og:image',
          content: imageUrl,
        },
        {
          property: 'og:image:height',
          content: imageHeight,
        },
        {
          property: 'og:image:width',
          content: imageWidth,
        },

        {
          property: 'twitter:card',
          content: twitterCard,
        },
        {
          property: 'twitter:site',
          content: twitterSite,
        },
        {
          property: 'twitter:title',
          content: title,
        },
        {
          property: 'twitter:description',
          content: description,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        },
      ]}
    />
  );
}

SEO.propTypes = {
  /** Identifier used to lookup up metadata associated with an app route */
  metadataId: PropTypes.string.isRequired,
  /**
   * Object containing values that are injected into metadata strings where
   * their keys match placeholders
   */
  dynamicValues: PropTypes.shape({}),
  /** If `true` the title suffix will be appended to the header title */
  useTitleSuffix: PropTypes.bool,
  /**
   * If `false` the page will not be indexed by search engine crawlers:
   * https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag
   */
  indexPage: PropTypes.bool,
};

SEO.defaultProps = {
  dynamicValues: {},
  useTitleSuffix: true,
  indexPage: true,
};

export default SEO;
